import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { LoaderCircle, Swipeable, Checkbox } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import buyoutResources from '@/api/buyout'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useText } from '@/components/i18n/hooks'
import Button from '@/components/util/Button'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { numberFormat } from '@/util/formatters/numbers'

import { Translation } from '../i18n/Translation'

const CHOOSE_ADDRESS = -1
const CUSTOM_ADDRESS = -2

const Order4Confirmation: FC<any> = ({
  addresses = [],
  setOrderAddress,
  order,
  inventory,
  settings,
  postOrder,
  display,
  orderError,
  orderFeedBack,
  cancelOrder = () => {},
}) => {
  const acceptTermsText = useText('Vennligst aksepter villkårene før du bekrefter bestillingen.')
  const chooseAddressText = useText('Vennligst velg en adresse.')
  const { formatNumber } = useFormattedNumberWithCurrency()
  const { whiteLabel } = useAppContext()

  const [isLoading, setIsLoading] = useState(false)
  const [isCustomAddress, setIsCustomAddress] = useState(false)
  const [isTermsConfirmed, setIsTermsConfirmed] = useState(false)
  const [isBuyout, setIsBuyout] = useState(false)

  const { data: buyoutDetails, isLoading: isBuyoutDetailsLoading } = useQuery(
    queries.buyout(),
    buyoutResources.get
  )

  const hasAddresses = () => Boolean(addresses?.length > 0)
  const isMissingAddress = () =>
    !!order && hasAddresses() && (!order.address || !order.zip || !order.city)

  // @ts-ignore
  const setAddress = e => {
    const { value } = e.target

    setIsCustomAddress(parseInt(value) === CUSTOM_ADDRESS)

    if ([CHOOSE_ADDRESS, CUSTOM_ADDRESS].includes(parseInt(value))) {
      setOrderAddress(null, null, null)
    } else {
      const address = addresses[value]
      setOrderAddress(address.address, address.zip, address.city)
    }
  }

  const confirm = () => {
    if (!isTermsConfirmed) {
      alert(acceptTermsText)
    } else {
      if (isMissingAddress()) {
        alert(chooseAddressText)
        return null
      }
      setIsLoading(true)
      postOrder(isBuyout)
    }
  }

  const mainProduct = order?.mainProduct
  let priceTotal = 0
  if (order && order.products.length > 0) {
    Object.keys(inventory).forEach(i => {
      const device = inventory[i]
      // @ts-ignore
      device.forEach(j => {
        if (order.products.indexOf(j.id) !== -1) {
          priceTotal += j.price
        }
      })
    })
  }

  if (isBuyoutDetailsLoading) return <LoaderCircle />
  if (!buyoutDetails) return null

  const { hasBuyableAsset, assetName, buyoutPrice } = buyoutDetails

  return (
    <Swipeable
      id="order"
      className={display ? 'is-visible' : 'is-hidden'}
      onSwipedLeft={cancelOrder}
    >
      <div className="header">
        <Translation id="Bekreft bestilling" />
      </div>

      <div className="order-detail">
        <div className="desc">
          <Translation id="Modell" />
        </div>
        <div className="val">{mainProduct ? mainProduct.model : ''}</div>
      </div>

      <div className="order-detail">
        <div className="desc">
          <Translation id="Totalpris" />
        </div>
        <div className="val">
          <FormattedNumberWithCurrency amount={priceTotal || 0} precision={2} />
        </div>
      </div>

      <div className="order-detail">
        <div className="desc">
          <Translation id="Å betale" />
        </div>
        <div className="val">
          <FormattedNumberWithCurrency amount={order?.remainder || 0} precision={2} />
        </div>
      </div>

      <div className="order-detail">
        <div className="desc">
          <Translation id="Lønnstrekk størrelse" />
        </div>
        <div className="val">
          <FormattedNumberWithCurrency amount={order?.monthlySalaryDeduction || 0} precision={2} />
        </div>
      </div>

      <div className="order-detail">
        <div className="desc">
          <Translation id="Antall lønnstrekk" />
        </div>
        <div className="val">{order ? numberFormat(order.downPaymentDuration) : ''}</div>
      </div>

      {hasAddresses() && (
        <div className="order-detail adr">
          <div className="desc">
            <Translation id="Leveranse adresse" />
          </div>
          <div className="val">
            <Addresses
              addresses={addresses}
              settings={settings}
              setAddress={setAddress}
              order={order}
            />
            {isCustomAddress && <CustomAddress setOrderAddress={setOrderAddress} />}
          </div>
        </div>
      )}

      {hasBuyableAsset && (
        <div className="old-asset">
          <h3>
            <Translation id="Hva vil du gjøre med din {assetName}?" values={{ assetName }} />
          </h3>
          <div>
            <Checkbox
              label={<Translation id="Levere inn til arbeidsgiver" />}
              checked={!isBuyout}
              onChange={() => setIsBuyout(false)}
              id="old_asset_return"
            />
          </div>
          <div>
            <Checkbox
              label={
                <Translation
                  id="Kjøpe ut, medfører {buyoutPrice} i lønnstrekk"
                  buyoutPrice={formatNumber({ amount: buyoutPrice })}
                />
              }
              checked={isBuyout}
              onChange={() => setIsBuyout(true)}
              id="old_asset_buyout"
            />
          </div>
        </div>
      )}

      <div
        className={
          'order-feedback ' +
          (orderError ? 'error' : 'success') +
          ' ' +
          (orderFeedBack ? 'is-visible' : 'is-hidden')
        }
      >
        <div className="header">
          <Translation id="Kunne ikke opprette ordre:" />
        </div>
        <div className="message">{orderFeedBack && <Translation id={orderFeedBack} />}</div>
      </div>

      <div className="terms">
        <Translation id="VILLKÅR FOR BESTILLING" />
        <br />
        <br />
        <Translation id="Arbeidstaker gir ved denne aksept arbeidsgiver fullmakt til å foreta lønntrekk for kostnader tilknyttet denne bestillingen. Jf. Arbeidsmiljøloven § 14-15 annet ledd bokstav c." />
        <br />
        <br />
        <Checkbox
          id="confirm-terms"
          label={<Translation id="Kryss av for å akseptere villkår for bestilling" />}
          checked={isTermsConfirmed}
          onChange={() => setIsTermsConfirmed(!isTermsConfirmed)}
        />
      </div>

      {isLoading ? (
        <div className="">
          <div className="loader" />
        </div>
      ) : (
        // TODO: refactor and use Button component from common lib
        <div className="buttons">
          <Button className={'button ' + whiteLabel} onClick={cancelOrder}>
            <Translation id="Avbryt" />
          </Button>
          <Button className={'button ' + whiteLabel} onClick={confirm} disabled={!isTermsConfirmed}>
            <Translation id="Bekreft" />
          </Button>
        </div>
      )}
    </Swipeable>
  )
}

const CustomAddress = ({ setOrderAddress = () => {} }) => {
  const [address, setAddress] = useState('')
  const [zip, setZip] = useState('')
  const [city, setCity] = useState('')

  const addressPlaceholder = useText('Adresse')
  const zipPlaceholder = useText('Postnr')
  const cityPlaceholder = useText('Sted')

  useEffect(() => {
    // @ts-ignore
    setOrderAddress(address, zip, city)
  }, [address, zip, city])

  return (
    <div className="custom-adress-input">
      <input
        className="address"
        type="text"
        value={address}
        onChange={e => setAddress(e.target.value)}
        placeholder={addressPlaceholder}
        autoComplete="street-address"
      />

      <input
        className="zip"
        type="text"
        maxLength={10}
        value={zip}
        onChange={e => setZip(e.target.value)}
        placeholder={zipPlaceholder}
        autoComplete="postal-code"
      />

      <input
        className="city"
        type="text"
        value={city}
        onChange={e => setCity(e.target.value)}
        placeholder={cityPlaceholder}
        autoComplete="address-level2"
      />
    </div>
  )
}

const Addresses = ({ addresses = [], settings, setAddress, order }: any) => {
  const { is_custom_address_activated: isCustomAddressActivated } = settings

  const chooseText = useText('VELG')
  const otherAddressText = useText('Annen adresse')

  return (
    <select onChange={setAddress} key={order ? order.products[0].id : 1} className="addresses">
      <option value={-1}>{chooseText}</option>
      {/* @ts-ignore */}
      {addresses.map((address, i) => (
        <option key={address.address + address.zip + address.city} value={i}>
          {address.address}, {address.zip} {address.city}
        </option>
      ))}

      {isCustomAddressActivated && <option value={-2}>{otherAddressText}</option>}
    </select>
  )
}

export default Order4Confirmation
